import Avatar from 'antd/lib/avatar';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import { compact } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link, useRouteMatch } from 'react-router-dom';
import { RootState } from '../../../reducers/reducers';
import routes from '../../../routes';
import { selectAllAppsIdsInOrg, selectApp, selectOrg, selectUser } from '../../../selectors/selectors';
import { AppRouteComponentMatchParams } from '../../../typescriptHelpers';
import Icon, { CaretDownFilled, CloseCircleFilled } from '@ant-design/icons';
import { antGrey } from '../../styles/globalStyles';
import { displayTextForAppPlatform, displayUserNameShort } from '../../../helpers/displayHelpers';
import LogoWordmark from '../../../../assets/runway-logo-horizontal.svg';
import styles from '../../styles/organizationNavigatorStyles';
import { iconComponentForPlatform } from '../../../helpers/platformComponentHelpers';
import { AppPlatform } from '../../../types';
import { createAppBuildsRoute, createOrgRoute } from '../../../helpers/routeHelpers';
import { getIsBuildDistroSupportedPlatform } from '../../../helpers';

export interface OrgOrAppMenuItemProps {
    shouldShowText?: boolean;
    onClick?: () => void;
    shouldShowCircleIcon?: boolean;
    appPlatform?: AppPlatform;
    title: string;
    link?: string;
    imageUrl: string;
    disabled?: boolean;
}

export const OrgOrAppMenuItem: React.FC<OrgOrAppMenuItemProps> = ({
    shouldShowText,
    onClick,
    shouldShowCircleIcon,
    title,
    link,
    imageUrl,
    appPlatform,
    disabled,
}) => {
    const firstLetter = title.charAt(0).toUpperCase();

    const contents = (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                justifyContent: 'space-between',
                flex: 1,
                minWidth: 0,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    gap: 18,
                    alignItems: 'center',
                    minWidth: 0,
                    opacity: disabled ? 0.5 : 1,
                    filter: disabled ? 'grayscale(100%)' : 'none',
                }}
            >
                <Avatar
                    shape={shouldShowCircleIcon ? 'circle' : 'square'}
                    size={27}
                    src={imageUrl}
                    style={{
                        flexShrink: 0,
                    }}
                >
                    {firstLetter}
                </Avatar>
                {shouldShowText && (
                    <div
                        style={{
                            display: 'flex',
                            gap: 8,
                            minWidth: 0,
                        }}
                    >
                        <Text
                            ellipsis
                            style={{
                                fontSize: 16,
                                color: antGrey.grey1,
                                minWidth: 0,
                            }}
                        >
                            {title}
                        </Text>

                        {appPlatform && (
                            <Text
                                ellipsis
                                style={{
                                    fontSize: 16,
                                    color: antGrey.grey7,
                                    wordBreak: 'normal',
                                    minWidth: 0,
                                }}
                            >
                                {displayTextForAppPlatform(appPlatform)}
                            </Text>
                        )}
                    </div>
                )}
            </div>
            {appPlatform && (
                <Icon
                    component={iconComponentForPlatform(appPlatform)}
                    style={{ color: antGrey.grey7, fontSize: 22 }}
                />
            )}
        </div>
    );

    if (link) {
        return (
            <Link
                onClick={onClick}
                to={link}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    pointerEvents: disabled ? 'none' : 'inherit',
                    justifyContent: 'space-between',
                    minWidth: 0,
                    ...(appPlatform ?
                        {
                            padding: '22px 20px',
                            backgroundColor: antGrey.grey8,
                        }
                    :   {}),
                }}
            >
                {contents}
            </Link>
        );
    }

    return contents;
};

export interface MobileOrgOrAppPickerProps {
    defaultOrgId: string;
}

export const MobileOrgOrAppPicker: React.FC<MobileOrgOrAppPickerProps> = ({ defaultOrgId }) => {
    const appRouteMatch = useRouteMatch<AppRouteComponentMatchParams>(routes.app);

    const selectedAppId = appRouteMatch ? appRouteMatch.params.appIdParam : null;

    const [isExpanded, setIsExpanded] = React.useState(false);
    const user = useSelector((state: RootState) => selectUser(state));
    const avatarDisplayName = displayUserNameShort(user).charAt(0).toUpperCase();

    const allAppsInOrg = useSelector((state: RootState) => {
        const allAppIdsInOrg = selectAllAppsIdsInOrg(state, defaultOrgId);
        const allApps = allAppIdsInOrg.map((appId) => selectApp(state, appId));
        return compact(allApps);
    });

    const org = useSelector((state: RootState) => selectOrg(state, defaultOrgId));

    const orgRoute = createOrgRoute(defaultOrgId);

    const selectedApp = useSelector((state: RootState) => selectApp(state, selectedAppId));

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '22px 20px',
                position: 'relative',
                flexGrow: 1,
                alignItems: 'center',
                width: '100vw',
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    opacity: isExpanded ? 1 : 0,
                    pointerEvents: isExpanded ? 'all' : 'none',
                    transition: 'all 0.2s',
                    top: '100%',
                    left: 0,
                    width: '100%',
                    zIndex: 10,
                    boxShadow: 'var(--rw-shadow-lg)',
                }}
            >
                {isExpanded && (
                    <Space
                        size={0}
                        direction="vertical"
                        style={{
                            width: '100%',
                        }}
                        split={
                            <div
                                style={{
                                    backgroundColor: antGrey.grey8,
                                    padding: '0 20px',
                                    height: 1,
                                }}
                            >
                                <div
                                    style={{
                                        height: 1,
                                        width: '100%',
                                        backgroundColor: antGrey.grey7,
                                    }}
                                />
                            </div>
                        }
                    >
                        {allAppsInOrg.map((app) => {
                            const appRoute = createAppBuildsRoute(defaultOrgId, app.appId);

                            const title = app.appSettings.appName;
                            const imageUrl = app.appSettings.appImageUrl;
                            const appPlatform = app.appSettings.platform;

                            const isBuildDistroSupportedPlatform = getIsBuildDistroSupportedPlatform(appPlatform);

                            return (
                                <OrgOrAppMenuItem
                                    shouldShowText
                                    key={app.appId}
                                    link={appRoute}
                                    title={title}
                                    imageUrl={imageUrl}
                                    appPlatform={appPlatform}
                                    disabled={!isBuildDistroSupportedPlatform}
                                    onClick={() => setIsExpanded(false)}
                                />
                            );
                        })}
                    </Space>
                )}
            </div>
            {isExpanded ?
                <>
                    <OrgOrAppMenuItem
                        shouldShowCircleIcon
                        shouldShowText
                        title={org.name}
                        imageUrl={org.orgImageUrl}
                        link={orgRoute}
                        onClick={() => setIsExpanded(false)}
                    />
                    <CloseCircleFilled
                        style={{
                            color: antGrey.grey1,
                            fontSize: 28,
                        }}
                        onClick={() => setIsExpanded(false)}
                    />
                </>
            :   <>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            cursor: 'pointer',
                        }}
                        onClick={() => setIsExpanded(true)}
                    >
                        <OrgOrAppMenuItem
                            title={selectedApp?.appSettings?.appName ?? org.name}
                            imageUrl={selectedApp?.appSettings?.appImageUrl ?? org.orgImageUrl}
                            shouldShowCircleIcon={!selectedApp}
                            onClick={() => setIsExpanded(false)}
                        />
                        <CaretDownFilled
                            style={{
                                color: antGrey.grey7,
                                fontSize: 22,
                            }}
                        />
                    </div>
                    <Space size={20}>
                        <img src={LogoWordmark} />
                        <Link to={routes.user}>
                            <Avatar size={28} style={{ ...styles.userAvatar }} src={user.commsAvatars?.image48}>
                                {avatarDisplayName}
                            </Avatar>
                        </Link>
                    </Space>
                </>
            }
        </div>
    );
};
